import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowUpRight, Check, Sparkles } from "lucide-react";
import { api, fmtUSD } from "@/lib/api";
import { PayTile } from "@/components/PaymentVisuals";
import PartnerLogos from "@/components/PartnerLogos";
import ArticleCover from "@/components/ArticleCover";
import { SidebPill } from "@/components/SidebPill";
import { Seo, organizationSchema, websiteSchema } from "@/components/Seo";

export default function Landing() {
  const [services, setServices] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    // Fetch once on mount; api / setters are stable module-level refs.
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-white" data-testid="landing-page">
      <Seo
        title="SideB · AI-First Operations & Solutions Management"
        description="sideb.io scales operations without adding headcount. Operating Partner & AI Solutions Management delivering vendor-agnostic blueprints to reduce SaaS bloat, automate processes, and build AI-first RevOps pipelines."
        keywords="Operating Partner, AI Solutions Management, AIOps Partner, Process Automation Consulting, Payment gateway optimization, AI-first RevOps consulting, Reduce SaaS bloat with internal AI, Scale operations without adding headcount"
        canonical="https://sideb.io/"
        schema={[organizationSchema(), websiteSchema()]}
      />
      {/* HERO — Deep Slate */}
      <section className="bg-ink relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-20 w-[700px] h-[700px] rounded-full opacity-30"
            style={{ background: "radial-gradient(circle, rgba(172,98,234,0.35) 0%, transparent 60%)" }} />
          <div className="absolute bottom-0 -left-40 w-[520px] h-[520px] rounded-full opacity-20"
            style={{ background: "radial-gradient(circle, rgba(214,197,240,0.5) 0%, transparent 65%)" }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur" data-testid="hero-eyebrow">
                <span className="w-1.5 h-1.5 rounded-full bg-[#ac62ea]" />
                <span className="text-[11px] text-white/80 font-medium uppercase tracking-widest">SideB &middot; the new side of revenue strategy</span>
              </div>
              <h1 className="font-display text-[44px] sm:text-6xl lg:text-7xl text-white font-semibold leading-[0.96] tracking-[-0.04em] mt-7">
                Revenue performance optimization.<br />Built for the <span className="text-[#ac62ea]">AI era.</span>
              </h1>
              <p className="mt-7 text-lg text-white/65 max-w-xl leading-relaxed">
                AI-first consulting for digital revenue and operations leaders. We diagnose your payments stack, modernize procurement, generate your RevOps playbooks, and deliver real-time revenue intelligence &mdash; so every dollar lands, and every model lifts.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-12 px-6 text-sm font-semibold" data-testid="hero-book-cta">
                  <Link to="/services">Browse services <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="btn-pill h-12 px-6 text-sm bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white" data-testid="hero-inquiry-cta">
                  <Link to="/inquiry">Explore a custom project</Link>
                </Button>
                <Button asChild variant="outline" className="btn-pill h-12 px-6 text-sm bg-transparent border-[#ac62ea]/40 text-[#ac62ea] hover:bg-[#ac62ea]/15 hover:text-[#ac62ea] hover:border-[#ac62ea]/70" data-testid="hero-parse-cta">
                  <Link to="/parse"><Sparkles className="mr-2 h-4 w-4" /> Optimize your AI</Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-4 relative h-[480px] hidden lg:block lg:col-start-9">
              <PartnerLogos />
            </div>
          </div>
        </div>

        <div className="relative border-t border-white/10" data-testid="hero-proof-stats">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-x-8 gap-y-3">
                <div className="flex items-baseline gap-2" data-testid="hero-stat-rxp">
                  <span className="font-display text-2xl text-white font-semibold tracking-tight">$300K+</span>
                  <span className="text-xs text-white/55 uppercase tracking-widest">saved · 5-yr partnership</span>
                </div>
                <div className="hidden sm:block w-1 h-1 rounded-full bg-white/20" aria-hidden />
                <div className="flex items-baseline gap-2" data-testid="hero-stat-revperf">
                  <span className="font-display text-2xl text-white font-semibold tracking-tight">1.5&times;</span>
                  <span className="text-xs text-white/55 uppercase tracking-widest">return-customer flywheel</span>
                </div>
                <div className="hidden sm:block w-1 h-1 rounded-full bg-white/20" aria-hidden />
                <div className="flex items-baseline gap-2" data-testid="hero-stat-studio">
                  <span className="font-display text-2xl text-white font-semibold tracking-tight">4&times;</span>
                  <span className="text-xs text-white/55 uppercase tracking-widest">fraud-rate reduction</span>
                </div>
              </div>
              <Link to="/proof" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#ac62ea] hover:text-white transition-colors group" data-testid="hero-proof-link">
                See the proof <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </div>

        <div className="relative border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <PayTile label="Pay by card or invoice" glyph="card" />
              <PayTile label="Quotes delivered in 48h avg." glyph="wallet" />
              <PayTile label="SOC2-grade vendor handling" glyph="shield" to="/security" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-b border-border" id="services">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <div className="label-mono">The catalog</div>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold mt-3 leading-[1.05] tracking-[-0.035em]">Engagements<br />that deliver.</h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
              <p className="text-muted-foreground leading-relaxed text-lg">
                Pick a fixed-scope session or a multi-week sprint. Every engagement ends with a measurable deliverable — a playbook, a model, a board-ready report. Pay online, no proposals to chase.
              </p>
              <Link to="/services" className="mt-5 inline-flex items-center gap-1 text-[#8a48cf] font-medium text-sm hover:underline" data-testid="services-link">
                See all engagements <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(() => {
              // Curated featured order on the home page.
              const FEATURED_SKUS = ["aiops-enablement", "revenue-diagnostic", "enablement-sprint"];
              const HIGHLIGHTED_SKU = "aiops-enablement";
              const bySku = Object.fromEntries(services.map((s) => [s.sku, s]));
              return FEATURED_SKUS.map((sku) => bySku[sku]).filter(Boolean).map((s, i) => {
                const highlighted = s.sku === HIGHLIGHTED_SKU;
                return (
                  <div
                    key={s.id}
                    className={
                      "card-sharp p-6 flex flex-col relative " +
                      (highlighted
                        ? "ring-2 ring-[#ac62ea] shadow-[0_18px_40px_-12px_rgba(172,98,234,0.35)] bg-gradient-to-br from-white via-white to-[#ac62ea]/[0.06]"
                        : "")
                    }
                    data-testid={`service-card-${i}`}
                  >
                    {highlighted && (
                      <div
                        className="absolute -top-3 left-5 px-2.5 py-1 rounded-full bg-[#ac62ea] text-white text-[10px] uppercase tracking-widest font-medium shadow-md"
                        data-testid={`service-card-featured-${i}`}
                      >
                        Featured
                      </div>
                    )}
                    <div className="label-mono">{s.category}</div>
                    <div className="font-display text-xl mt-3 leading-tight font-semibold">{s.title}</div>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-3 flex-1">{s.description}</p>
                    <div className="flex items-end justify-between mt-6 pt-4 border-t border-border">
                      <div>
                        <div className="label-mono">{s.quote_only ? "Pricing" : "Investment"}</div>
                        <div className="font-display text-2xl mt-0.5 font-semibold">{s.quote_only ? <span className="text-lg">On request</span> : fmtUSD(s.price)}</div>
                      </div>
                      <Button
                        asChild
                        size="sm"
                        variant="ghost"
                        className={highlighted
                          ? "rounded-full bg-[#ac62ea] text-white hover:bg-[#8a48cf] hover:text-white"
                          : "rounded-full hover:text-[#8a48cf] hover:bg-[#ac62ea]/10"}
                      >
                        <Link to={`/services/${s.sku}`} data-testid={`service-card-link-${i}`}>
                          {s.quote_only ? "Get quote" : "Book"} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          {services.length > 3 && (
            <div className="mt-10 flex justify-center" data-testid="see-all-engagements-row">
              <Button asChild className="rounded-full bg-[#0A0A0A] hover:bg-black/85 h-11 px-6" data-testid="see-all-engagements-cta">
                <Link to="/services">See all engagements <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="bg-[#0a1628] text-white relative overflow-hidden" data-testid="proof-strip">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          aria-hidden
          style={{
            background: "radial-gradient(50% 50% at 80% 50%, rgba(172,98,234,0.45) 0%, rgba(11,30,90,0) 70%)",
            filter: "blur(40px)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          {/* Header row */}
          <div className="grid md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-8">
              <div className="label-mono text-white/60">Proof · Selected case studies</div>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 tracking-[-0.03em] leading-[1.05] font-semibold">
                Consulting that pays back — <span className="text-[#c9a3f4]">and keeps paying.</span>
              </h3>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-6" data-testid="proof-strip-cta">
                <Link to="/proof">See case studies <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>

          {/* Tease tiles */}
          <div className="mt-10 lg:mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {[
              {
                slug: "rxp",
                label: "Procurement / RFP Project",
                value: "A 5-year compounding payments partnership.",
                href: "/proof/rxp",
                testId: "proof-strip-rxp-link",
              },
              {
                slug: "revenue-performance",
                label: "Payments Optimization Sprint",
                value: "+$18M recovered, a 1.5x return-customer flywheel.",
                href: "/proof/revenue-performance",
                testId: "proof-strip-revperf-link",
              },
              {
                slug: "aiops",
                label: "AI Operating Partner",
                value: "Real-time margin visibility, baked into the operating loop.",
                href: "/proof/aiops",
                testId: "proof-strip-aiops-link",
              },
              {
                slug: "studio",
                label: "Studio Retainer",
                value: "Crisis-grade payments operator, kept on the bench.",
                href: "/proof/studio-retainer",
                testId: "proof-strip-studio-link",
              },
            ].map((t) => (
              <Link
                key={t.slug}
                to={t.href}
                data-testid={t.testId}
                className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] hover:border-[#c9a3f4]/40 p-5 lg:p-6 transition-all duration-300"
              >
                <div className="label-mono text-white/55">{t.label}</div>
                <p className="font-display text-lg lg:text-xl mt-2 leading-snug tracking-tight text-white/95">{t.value}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#c9a3f4] group-hover:text-white transition-colors">
                  Read the case study
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section className="border-b border-border bg-ice-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="label-mono mb-3">Method</div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-[-0.035em] max-w-3xl leading-[1.05]">A three-phase operating loop.</h2>
          <p className="text-muted-foreground mt-5 max-w-2xl text-lg">From diagnosis to durable system. Each phase ties to a specific engagement on the catalog — pick the entry point that matches where you are.</p>
          <div className="mt-14 grid md:grid-cols-3 gap-px bg-border">
            {[
              {
                num: "01",
                title: "Diagnose",
                body: "Deep-dive into your revenue operations and payment stack. Identify product gaps, vendor risk and the actionable items that move margin.",
                engagements: [
                  { name: "Revenue Diagnostic", path: "/services" },
                ],
              },
              {
                num: "02",
                title: "Benchmark",
                body: "Quantify your acceptance costs and success rates against operators in your category. Numbers your CFO can defend.",
                engagements: [
                  { name: "Payments Diagnostic", path: "/services" },
                ],
              },
              {
                num: "03",
                title: "Operationalize",
                body: "Translate findings into a vendor decision, costs roadmap or year-round operating cadence. You leave with a working asset, not a binder.",
                engagements: [
                  { name: "Payments Optimization Sprint", path: "/services" },
                  { name: "Procurement / RFP Project", path: "/services" },
                  { name: "Studio Retainer", path: "/services" },
                ],
              },
            ].map(({ num, title, body, engagements }) => (
              <div key={num} className="bg-white p-10 flex flex-col" data-testid={`method-phase-${num}`}>
                <div className="font-mono text-[11px] text-[#8a48cf] font-semibold tracking-widest">PHASE {num}</div>
                <div className="font-display text-2xl mt-3 font-semibold">{title}</div>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">{body}</p>
                <div className="mt-6 pt-5 border-t border-border">
                  <div className="label-mono mb-2">Engagements</div>
                  <ul className="space-y-1.5">
                    {engagements.map((e) => (
                      <li key={e.name}>
                        <Link to={e.path} className="text-sm font-medium text-foreground hover:text-[#8a48cf] inline-flex items-center gap-1 group">
                          {e.name}
                          <ArrowUpRight className="h-3.5 w-3.5 text-foreground/40 group-hover:text-[#8a48cf] transition-colors" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RETAINER */}
      <section className="bg-ink text-white border-b border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full opacity-25"
            style={{ background: "radial-gradient(circle, rgba(172,98,234,0.6) 0%, transparent 60%)" }} />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/[0.05]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ac62ea]" />
              <span className="text-[11px] text-white/85 font-medium uppercase tracking-widest">New · Studio Retainer</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold mt-6 tracking-[-0.035em] leading-[1.05]">
              Keep us on the bench.<br />Year-round.
            </h2>
            <p className="text-white/65 mt-6 max-w-xl leading-relaxed text-lg">
              For teams that want <SidebPill variant="dark" /> in the room year-round. Every retainer is mutually scoped at activation — the examples on the right show what that often looks like in practice. We got you when you need us — cancel anytime.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-12 px-6 font-semibold" data-testid="retainer-cta">
                <Link to="/services/studio-retainer">Activate retainer <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <div className="text-sm text-white/55">
                <span className="text-white font-semibold">Custom quote</span> · cancel anytime
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-white/10 rounded-2xl p-7 bg-white/[0.03] backdrop-blur">
              <div className="label-mono" style={{ color: "rgba(255,255,255,0.5)" }}>How teams typically use us</div>
              <p className="text-white/85 mt-3 text-sm font-medium">We got you — on the call, in the negotiation, across the org.</p>
              <ul className="mt-5 space-y-3.5 text-sm">
                {[
                  "AI-informed market intelligence & competitive positioning",
                  "Strategic supplier economics & intelligent partner management",
                  "Predictive revenue modeling & monthly recurring performance reports",
                  "Plug-and-play operating leadership for AI-first organizations",
                  "Comprehensive revenue technology & automation reviews",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#ac62ea]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-[#ac62ea]" strokeWidth={3} />
                    </div>
                    <span className="text-white/90">{line}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 pt-5 border-t border-white/10 text-xs text-white/55 leading-relaxed" data-testid="retainer-scoping-note">
                Each retainer is scoped at activation and subject to the <a href="/terms#services" className="text-[#ac62ea] hover:underline">Online Services Agreement</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INSIGHTS */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="label-mono">Insights</div>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold mt-3 tracking-[-0.035em]">Notes from the field.</h2>
            </div>
            <Link to="/insights" className="text-[#8a48cf] font-medium text-sm hover:underline flex items-center gap-1" data-testid="articles-link">
              All insights <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.slice(0, 3).map((a, i) => (
              <Link to={`/insights/${a.slug}`} key={a.id} className="card-sharp overflow-hidden flex flex-col group" data-testid={`article-card-${i}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <ArticleCover article={a} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-[#8a48cf] font-medium uppercase tracking-widest">{a.category}</span>
                  </div>
                  <h3 className="font-display text-xl mt-3 font-semibold tracking-tight leading-snug">{a.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-2">{a.subtitle}</p>
                  <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{a.author}</span>
                    <ArrowUpRight className="h-4 w-4 text-foreground/40 group-hover:text-[#8a48cf] transition-colors" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section className="border-b border-border bg-ice-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-mono">What you leave with</div>
            <h2 className="font-display text-4xl font-semibold mt-3 tracking-[-0.035em] leading-[1.05]">Deliverables,<br />not decks.</h2>
            <p className="text-muted-foreground mt-5 leading-relaxed">
              Every engagement produces a tangible, owner-shippable deliverable — built for the person running the business after we leave.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              "Payment stack diagnostic with prioritized actions",
              "Strategy write-up with prioritized next steps",
              "Costs review & optimization roadmap",
              "Acceptance economics benchmarking report",
              "Vendor capability matrix, scoring rubric & negotiation playbook",
              "Board-prep review & quarterly performance read-out",
            ].map((d) => (
              <div key={d} className="flex items-start gap-3 p-4 bg-white border border-border rounded-xl">
                <div className="w-5 h-5 rounded-full bg-[#ac62ea]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="h-3 w-3 text-[#8a48cf]" strokeWidth={3} />
                </div>
                <span className="text-sm">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-mono" style={{ color: "rgba(255,255,255,0.55)" }}>Encore</div>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold mt-3 tracking-[-0.035em] leading-[1.05]">Your next quarter is waiting.</h2>
            <p className="text-white/65 mt-6 max-w-lg leading-relaxed">
              Book a session, or send a brief and we&apos;ll quote you within 48 hours.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
            <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-12 px-6 font-semibold" data-testid="bottom-book-cta">
              <Link to="/services">See engagements</Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill h-12 px-6 border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="bottom-inquiry-cta">
              <Link to="/inquiry">Send a brief</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
